import { PLATFORM_DEFAULTS } from '@concord/config';
import { formatBytes, rejectionFor } from '@concord/domain';
import type { CaseDetailView } from '@concord/domain/contracts';
import { Button, Dialog, Field, Notice, useToast } from '@concord/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ApiError, api, errorMessage } from '../../lib/api';
import { caseKey } from './CasePage';

const RULES = { maxBytes: PLATFORM_DEFAULTS.uploadMaxBytes, types: PLATFORM_DEFAULTS.uploadAcceptedTypes };

/**
 * DOC-06: the navigator attaches a record received outside the platform (fax, email, a
 * disc from another hospital) and says where it came from. After release it is attached to
 * the consultation at once, so the physicians see it on their link.
 */
export function AddRecordDialog({ c, onDone }: { c: CaseDetailView; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [source, setSource] = useState('');
  const [error, setError] = useState<string | null>(null);
  const released = c.state !== 'submitted' && c.state !== 'on_hold';

  const add = useMutation({
    mutationFn: async () => {
      const f = file!;
      const { uploadUrl } = await api<{ id: string; uploadUrl: string }>(`/cases/${c.id}/attachments`, {
        body: {
          filename: f.name,
          sizeBytes: f.size,
          contentType: f.type || 'application/octet-stream',
          description: description.trim(),
          externalSource: source.trim(),
        },
      });
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        credentials: 'same-origin',
        headers: { 'content-type': f.type || 'application/octet-stream' },
        body: f,
      });
      if (!res.ok) {
        const e = (await res.json().catch(() => null)) as {
          error?: { code?: string; message?: string };
        } | null;
        throw new ApiError(
          res.status,
          e?.error?.code ?? 'upload_failed',
          e?.error?.message ?? 'The upload failed.',
        );
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: caseKey(c.reference ?? '') });
      toast(released ? 'Record added and shared with the panel.' : 'Record added.');
      onDone();
    },
    onError: (e) => setError(errorMessage(e)),
  });

  const pick = (f: File | null) => {
    setError(f ? rejectionFor(f.name, f.size, f.type, RULES) : null);
    setFile(f);
  };
  const ready = file && !error && description.trim() && source.trim();

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onDone()}
      title="Add a record"
      description={
        released
          ? 'For records that reached you outside the platform. The case is released, so the record is attached to the consultation at once and the physicians see it on their link. It cannot be changed afterward.'
          : 'For records that reached you outside the platform. The patient sees it with their own records.'
      }
      footer={
        <>
          <Button variant="sec" onClick={onDone}>
            Cancel
          </Button>
          <Button busy={add.isPending} disabled={!ready} onClick={() => add.mutate()}>
            Add record
          </Button>
        </>
      }
    >
      <Field label="File" hint={`PDF, JPG or PNG, up to ${formatBytes(RULES.maxBytes)}.`}>
        {(id, d) => (
          <input
            id={id}
            aria-describedby={d}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
          />
        )}
      </Field>
      <Field label="Description" hint="What it is and when, as the physicians should read it.">
        {(id, d) => (
          <input
            id={id}
            aria-describedby={d}
            className="input"
            maxLength={300}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. PET-CT report, February 2026"
          />
        )}
      </Field>
      <Field label="Where it came from" hint="Recorded with the file and in the case log.">
        {(id, d) => (
          <input
            id={id}
            aria-describedby={d}
            className="input"
            maxLength={200}
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="e.g. Faxed by Dr. Patel's office"
          />
        )}
      </Field>
      {error && <Notice tone="bad">{error}</Notice>}
    </Dialog>
  );
}
